import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ChevronDown, Trash2, Pencil, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import {
  getPlantSafetyWorkPermitById,
  deletePlantSafetyWorkPermit,
  getAppointedOperators,
  notifyIsolationInstructions,
  getAppointedPersonsLV,
  getPlantSafetyWorkPermitDownload,
  getSanctionForTestDownload,
} from '../../../services/plantSafetyWorkPermitService';
import {
  addInstruction,
  deleteInstruction,
  editInstruction,
  getLvwpIsolationStatus,
  getLvwpSubstationKeys,
  markAOClearance,
  markAOCSuspended,
  markPermitTakeOut,
  markRPClearance,
  markRPSuspended,
  notifyAOCSuspend,
} from '../../../services/lvWorkPermitFlowService';
import { getPlantcodes, getLocation } from '../../../services/locationService';
import Header from '../../../components/Header';
import Activities from '../../../components/Activities/Activity';
import { markIsolation, markIssuePermit, notifyIssuePermit } from '../../../services/lvWorkPermitFlowService';
import Loader from '../../../components/Loader/Loader';
import SanctionForTestCard from './Sanction';
import AOClearanceModal from './AOClearanceModal';
import SanctionContent from './SanctionContent';
import AOCSignModal from './AOCSignModal';
import RPRevokeModal from './RPRevokeModal';
import NotifyAOCRevokeModal from './NotifyAOCRevokeModal';
import AOCRevokeModal from './AOCRevokeModal';
import RejectButton from '../../../components/RejectButton/RejectButton';
import CreatePlantPermitModal from '../../../components/CreatePlantSafetyPermit';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface WorkPermitDetail {
  id: number;
  reference_number: string;
  status: string;
  department: string;
  priority: string;
  job_number: string;
  ewt: string;
  location: string;
  unit: string;
  plant_code: string;
  plant_requiring_isolation: string;
  hazards: string[];
  work_to_be_carried_out: string[];
  ptw_no: string;
  environment_cert_no: string;
  gas_test_cert_no: string;
  hot_work_approval_part: string;
  hot_work_approval: string;
  risk_assessment_no: string;
  safe_entry_certificate: string;
  workers_register_no: string;
  work_register_number: string;
  ppe: string;
  isolations: { code: string }[];
  activities: {
    type: string;
    label: string;
    users: { name: string; role: string; avatar?: string }[];
  }[];
  created_by: any;
  workflow_stages: any;
  sanction?: Sanction;
  plant?: Plant;
  permit_number?: string;
  isolation?: {
    plant_code_id: number;
    location_id: number;
    describe_components?: string | null;
    decsribe_components: string | null;
    requirements_checked: boolean;
    items: {
      id: number;
      lvwp_isolation_id: number;
      plant_code_id?: number | null;
      location_id?: number | null;
      describe_components?: string | null;
      status_id: number;
      key_id: number;
      ops: string;
    }[];
  };
}

interface Plant {
  id: number;
  name: string;
}

interface Status {
  id: number;
  code: string;
  title: string;
  color: string;
  created_at: string;
  updated_at: string;
}

interface SanctionDetails {
  id: number;
  lv_work_permit_id: number;
  plant_code_id: number;
  job_number: string;
  work: string;
  allowed_work: string;
  isolation: string;
  init_signal: string;
  halt_signal: string;
  isolation_signal: string;
  status_id: number;
  created_at: string;
  updated_at: string;
  status: Status;
}

interface Sanction {
  details: SanctionDetails;
  status: Status;
}

interface AppointedOperator {
  id: number;
  name: string;
  role: string;
  avatar?: string;
}

interface InstructionItem {
  id: number | string | null;
  plant_code_id: number;
  location_id: number;
  describe_components: string;
  status_id: number;
  key_id: number;
  ops: string;
  isNew?: boolean;
}

const PlantSafetyWorkPermitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<WorkPermitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expandedIsolation, setExpandedIsolation] = useState<number | null>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [showError, setShowError] = useState(false);
  const [apiError, setApiError] = useState('');

  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showDeleteInstructionModal, setShowDeleteInstructionModal] = useState(false);
  const [deleteInstructionLoading, setDeleteInstructionLoading] = useState(false);
  const [notifyDropdownOpen, setNotifyDropdownOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<AppointedOperator | null>(null);
  const [appointedOperators, setAppointedOperators] = useState<AppointedOperator[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [operatorsError, setOperatorsError] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [showNotifySent, setShowNotifySent] = useState(false);

  const [showIssuePermitModal, setShowIssuePermitModal] = useState(false);
  const [issuePermitDropdownOpen, setIssuePermitDropdownOpen] = useState(false);
  const [selectedIssuePermitPerson, setSelectedIssuePermitPerson] = useState<AppointedOperator | null>(null);
  const [issuePermitLoading, setIssuePermitLoading] = useState(false);

  const [showIsolationModal, setShowIsolationModal] = useState(false);

  const [showIssuePermitConfirmModal, setShowIssuePermitConfirmModal] = useState(false);
  const [issuePermitProcessing, setIssuePermitProcessing] = useState(false);
  const [locations, setLocations] = useState([]);
  const [plantCodes, setPlantCodes] = useState([]);

  const [statusList, setStatusList] = useState([]);
  const [keyList, setKeyList] = useState([]);

  const [showPermitTakeOutModal, setShowPermitTakeOutModal] = useState(false);
  const [permitTakeOutLoading, setPermitTakeOutLoading] = useState(false);

  const [showSuspendPTWModal, setShowSuspendPTWModal] = useState(false);
  const [suspendPTWLoading, setSuspendPTWLoading] = useState(false);

  const [showRPClearanceModal, setShowRPClearanceModal] = useState(false);
  const [rpClearanceLoading, setRPClearanceLoading] = useState(false);

  const [showAOCClearanceModal, setShowAOCClearanceModal] = useState(false);
  const [aocClearanceLoading, setAOCClearanceLoading] = useState(false);

  const [showNotifySuspendModal, setShowNotifySuspendModal] = useState(false);
  const [notifySuspendDropdownOpen, setNotifySuspendDropdownOpen] = useState(false);
  const [selectedSuspendOperator, setSelectedSuspendOperator] = useState<AppointedOperator | null>(null);
  const [notifySuspendLoading, setNotifySuspendLoading] = useState(false);

  const [showAOCSuspendModal, setShowAOCSuspendModal] = useState(false);
  const [aocSuspendLoading, setAOCSuspendLoading] = useState(false);

  const [showAOClearanceModal, setShowAOClearanceModal] = useState(false);

  const [showRPRevokeModal, setShowRPRevokeModal] = useState(false);
  const [showNotifyAOCRevokeModal, setShowNotifyAOCRevokeModal] = useState(false);
  const [showAOCRevokeModal, setShowAOCRevokeModal] = useState(false);

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [sftDownloadLoading, setSftDownloadLoading] = useState(false);

  // ── Edit Modal ──
  const [showEditModal, setShowEditModal] = useState(false);

  // ── Edit Instructions mode flag (distinguishes Add vs Edit inside the same modal) ──
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);

  const [instructionForm, setInstructionForm] = useState({
    plantCode: '',
    location: '',
    description: '',
    requirement: false,
    statusId: '',
    keyId: '',
    ops: '',
  });

  const [instructionErrors, setInstructionErrors] = useState({
    plantCode: '',
    location: '',
    statusId: '',
    keyId: '',
    ops: '',
  });

  const [instructionLoading, setInstructionLoading] = useState(false);

  // ── Multi-item support for the Add/Edit Instructions modal ──
  const [instructionItems, setInstructionItems] = useState<InstructionItem[]>([]);
  const [editingInstructionItemId, setEditingInstructionItemId] = useState<number | string | null>(null);
  const [expandedInstructionItem, setExpandedInstructionItem] = useState<number | null>(null);

  const loadDropdowns = async () => {
    getPlantcodes();
    try {
      const [plantRes, locationRes] = await Promise.all([getPlantcodes(), getLocation()]);

      setPlantCodes(plantRes?.data || plantRes || []);
      setLocations(locationRes?.data || locationRes || []);
    } catch (err) {
      console.error('Dropdown load failed:', err);
    }
  };

  const loadInstructionDropdowns = async () => {
    try {
      const [statusRes, keyRes] = await Promise.all([getLvwpIsolationStatus(), getLvwpSubstationKeys()]);

      setStatusList(statusRes || []);
      setKeyList(keyRes || []);
    } catch (error) {
      console.error('Failed to load instruction dropdowns', error);
    }
  };

  const currentUserID = Number(localStorage.getItem('currentUserID'));

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  useEffect(() => {
    loadDropdowns();
    loadInstructionDropdowns();
  }, []);

  useEffect(() => {
    fetchAppointedOperators();
  }, []);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await getPlantSafetyWorkPermitById(id);
      const item = response?.data || response;
      const mapped: WorkPermitDetail = {
        id: item.id,
        reference_number: item.reference_number,
        status: item.status,
        department: item.department,
        priority: item.priority,
        job_number: item.job_number,
        ewt: item.ewt,
        location: item.location,
        unit: item.unit,
        plant_code: item.plant_code,
        plant_requiring_isolation: item.plant_requiring_isolation,
        hazards: item.description ? [item.description] : [],

        work_to_be_carried_out: item.work ? [item.work] : [],
        ptw_no: item.ptw_no,
        environment_cert_no: item.environment_cert_no,
        gas_test_cert_no: item.gas_test_cert_no,
        hot_work_approval_part: item.hot_work_approval_part,
        hot_work_approval: item.hot_work_approval,
        risk_assessment_no: item.risk_assessment_no,
        safe_entry_certificate: item.safe_entry_certificate,
        workers_register_no: item.workers_register_no,
        work_register_number: item.work_register_number,
        ppe: item.ppe,
        isolations: Array.isArray(item.isolations) ? item.isolations : [],
        activities: Array.isArray(item.activities) ? item.activities : [],
        created_by: item.created_by,
        workflow_stages: item.workflow_stages,
        sanction: item.sanction,
        permit_number: item.reference_number,
        isolation: item.isolation,
      };
      setDetail(mapped);
    } catch (error) {
      console.error('Failed to fetch work permit detail', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status: string) => {
    if (status === 'Isolation Effected') return { dot: 'bg-blue-500', text: 'text-blue-600' };
    if (status === 'New') return { dot: 'bg-purple-500', text: 'text-purple-600' };
    if (status === 'Permit Accepted') return { dot: 'bg-pink-500', text: 'text-pink-600' };
    if (status === 'Permit Issued') return { dot: 'bg-fuchsia-500', text: 'text-fuchsia-600' };
    if (status === 'Clearance Done (AO)') return { dot: 'bg-green-500', text: 'text-green-600' };
    return { dot: 'bg-gray-400', text: 'text-gray-600' };
  };

  const handleRPClearance = async () => {
    if (!id) return;

    try {
      setRPClearanceLoading(true);

      await markRPClearance(id);

      setShowRPClearanceModal(false);

      setSuccessMessage('Clearance completed successfully.');
      setShowSuccess(true);

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to complete clearance';

      setApiError(message);
      setShowError(true);
    } finally {
      setRPClearanceLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePlantSafetyWorkPermit(id);
      setShowDeleteModal(false);
      setSuccessMessage('Work permit successfully deleted.');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate(-1);
      }, 2000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete work permit.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    }
  };

  const handleDeleteInstruction = async () => {
    if (!id) return;

    try {
      setDeleteInstructionLoading(true);
      await deleteInstruction(id);
      setShowDeleteInstructionModal(false);
      setSuccessMessage('Instruction deleted successfully.');
      setShowSuccess(true);
      await fetchDetail();
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete instruction.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setDeleteInstructionLoading(false);
    }
  };

  const fetchAppointedOperators = async () => {
    try {
      setOperatorsLoading(true);
      const response = await getAppointedPersonsLV();
      const data = Array.isArray(response) ? response : response?.data || [];
      const mappedOperators: AppointedOperator[] = Array.isArray(data)
        ? data.map((operator: any) => ({
            id: operator.id,
            name: operator.name || `${operator.first_name || ''} ${operator.last_name || ''}`.trim() || 'Unknown',
            role: operator.role || operator.user_type || 'Appointed Operator',
            avatar: operator.avatar || operator.profile_image || operator.avatar_url,
          }))
        : [];
      setAppointedOperators(mappedOperators);
      setOperatorsError('');
    } catch (error: any) {
      console.error('Failed to load appointed operators', error);
      setOperatorsError(error?.response?.data?.message || error?.message || 'Unable to load appointed operators.');
      setAppointedOperators([]);
    } finally {
      setOperatorsLoading(false);
    }
  };

  // ── Notify Done Handler ──
  const handleNotifyDone = async () => {
    if (!selectedOperator || !id) return;

    try {
      setNotifyLoading(true);
      const payload = { user_ids: [selectedOperator.id] };
      await notifyIsolationInstructions(id, payload);
      await fetchDetail();
      setShowNotifyModal(false);
      setNotifyDropdownOpen(false);
      setShowNotifySent(true);
      setTimeout(() => {
        setShowNotifySent(false);
        setSelectedOperator(null);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to send notification.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setNotifyLoading(false);
    }
  };

  // ── Adds the current Status/Key/OPS row to the local items list (or updates it, if editing) ──
  const handleAddInstructionItem = () => {
    const errors = { ...instructionErrors, plantCode: '', location: '', statusId: '', keyId: '', ops: '' };
    let isValid = true;

    if (!instructionForm.plantCode) {
      errors.plantCode = 'Plant Code is required';
      isValid = false;
    }
    if (!instructionForm.location) {
      errors.location = 'Location is required';
      isValid = false;
    }
    if (!instructionForm.statusId) {
      errors.statusId = 'Status is required';
      isValid = false;
    }
    if (!instructionForm.keyId) {
      errors.keyId = 'Key is required';
      isValid = false;
    }
    if (!instructionForm.ops.trim()) {
      errors.ops = 'OPS is required';
      isValid = false;
    }

    setInstructionErrors(errors);
    if (!isValid) return;

    const itemData: InstructionItem = {
      id: editingInstructionItemId,
      plant_code_id: Number(instructionForm.plantCode),
      location_id: Number(instructionForm.location),
      describe_components: instructionForm.description,
      status_id: Number(instructionForm.statusId),
      key_id: Number(instructionForm.keyId),
      ops: instructionForm.ops,
    };

    setInstructionItems((prev) =>
      editingInstructionItemId !== null
        ? prev.map((it) => (it.id === editingInstructionItemId ? { ...itemData, isNew: it.isNew } : it))
        : [...prev, { ...itemData, id: Date.now(), isNew: true }],
    );

    setEditingInstructionItemId(null);

    setInstructionForm((prev) => ({
      ...prev,
      plantCode: '',
      location: '',
      description: '',
      statusId: '',
      keyId: '',
      ops: '',
    }));
  };

  const handleEditInstructionItem = (item: InstructionItem) => {
    setEditingInstructionItemId(item.id);
    setInstructionForm((prev) => ({
      ...prev,
      plantCode: String(item.plant_code_id),
      location: String(item.location_id),
      description: item.describe_components || '',
      statusId: item.status_id?.toString() || '',
      keyId: item.key_id?.toString() || '',
      ops: item.ops || '',
    }));
    setInstructionErrors((prev) => ({ ...prev, statusId: '', keyId: '', ops: '' }));
    setExpandedInstructionItem(null);
  };

  const handleCancelEditInstructionItem = () => {
    setEditingInstructionItemId(null);
    setInstructionForm((prev) => ({
      ...prev,
      plantCode: '',
      location: '',
      description: '',
      statusId: '',
      keyId: '',
      ops: '',
    }));
  };

  const handleRemoveInstructionItem = (itemId: number | string | null) => {
    setInstructionItems((prev) => prev.filter((it) => it.id !== itemId));
    if (editingInstructionItemId === itemId) {
      handleCancelEditInstructionItem();
    }
  };

  // ── Resets the Add/Edit Instructions modal back to a clean "Add" state ──
  const resetInstructionsModalState = () => {
    setShowInstructionsModal(false);
    setIsEditingInstructions(false);
    setInstructionForm({
      plantCode: '',
      location: '',
      description: '',
      requirement: false,
      statusId: '',
      keyId: '',
      ops: '',
    });
    setInstructionErrors({
      plantCode: '',
      location: '',
      statusId: '',
      keyId: '',
      ops: '',
    });
    setInstructionItems([]);
    setEditingInstructionItemId(null);
  };

  // ── Opens the modal in "Add" mode ──
  const handleOpenAddInstructions = () => {
    setIsEditingInstructions(false);
    setInstructionForm({
      plantCode: '',
      location: '',
      description: '',
      requirement: false,
      statusId: '',
      keyId: '',
      ops: '',
    });
    setInstructionItems([]);
    setEditingInstructionItemId(null);
    setShowInstructionsModal(true);
  };

  // ── Opens the modal in "Edit" mode, pre-filled from the existing isolation record ──
  const handleOpenEditInstructions = () => {
    if (!detail?.isolation) return;

    setInstructionForm({
      plantCode: '',
      location: '',
      description: '',
      requirement: Boolean(detail.isolation.requirements_checked),
      statusId: '',
      keyId: '',
      ops: '',
    });

    const existingItems: InstructionItem[] = (detail.isolation.items || []).map((item) => ({
      id: item.id,
      plant_code_id: item.plant_code_id ?? detail.isolation!.plant_code_id,
      location_id: item.location_id ?? detail.isolation!.location_id,
      describe_components: item.describe_components ?? detail.isolation!.describe_components ?? detail.isolation!.decsribe_components ?? '',
      status_id: item.status_id,
      key_id: item.key_id,
      ops: item.ops,
      isNew: false,
    }));

    setInstructionItems(existingItems);
    setEditingInstructionItemId(null);
    setInstructionErrors({
      plantCode: '',
      location: '',
      statusId: '',
      keyId: '',
      ops: '',
    });
    setIsEditingInstructions(true);
    setShowInstructionsModal(true);
  };

  // ── Final submit — sends plantCode/location/description/requirement + ALL collected items ──
  // Routes to addInstruction or editInstruction depending on isEditingInstructions.
  const handleSubmitAllInstructions = async () => {
    const errors = { ...instructionErrors };
    let isValid = true;

    setInstructionErrors(errors);

    if (editingInstructionItemId !== null) {
      // block submit while an item edit is in progress
      isValid = false;
    }

    if (instructionItems.length === 0) {
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    try {
      setInstructionLoading(true);

      const firstItem = instructionItems[0];
      const payload = {
        requirements_checked: instructionForm.requirement,
        remarks: instructionForm.requirement ? 'Requirements Checked' : '',

        plant_code_id: firstItem.plant_code_id,

        location_id: firstItem.location_id,

        describe_components: firstItem.describe_components,

        items: instructionItems.map((it) => ({
          ...(it.isNew ? {} : { id: it.id }),
          plant_code_id: it.plant_code_id,
          location_id: it.location_id,
          describe_components: it.describe_components,
          status_id: it.status_id,
          key_id: it.key_id,
          ops: it.ops,
        })),
      };

      // A rejected permit keeps its previous isolation record. When instructions
      // are submitted again, update that record so the new set replaces the
      // rejected instructions instead of creating a second, stale record.
      const isReplacingRejectedInstructions = !isEditingInstructions && Boolean(detail?.isolation);

      if (isEditingInstructions || isReplacingRejectedInstructions) {
        await editInstruction(id, payload);
      } else {
        await addInstruction(id, payload);
      }

      setSuccessMessage(
        isEditingInstructions || isReplacingRejectedInstructions ? 'Instruction updated successfully' : 'Instruction added successfully',
      );
      setShowSuccess(true);

      resetInstructionsModalState();

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      setApiError(
        error?.response?.data?.message ||
          error?.message ||
          (isEditingInstructions ? 'Failed to update instruction' : 'Failed to add instruction'),
      );

      setShowError(true);

      setTimeout(() => {
        setShowError(false);
      }, 5000);
    } finally {
      setInstructionLoading(false);
    }
  };

  const handleNotifySuspendDone = async () => {
    if (!selectedSuspendOperator || !id) return;

    try {
      setNotifySuspendLoading(true);

      const payload = {
        user_ids: [selectedSuspendOperator.id],
      };

      await notifyAOCSuspend(id, payload);

      setShowNotifySuspendModal(false);

      setSuccessMessage('Suspension notification sent successfully.');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setSelectedSuspendOperator(null);
      }, 3000);

      await fetchDetail();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to send suspension notification';

      setApiError(message);
      setShowError(true);
    } finally {
      setNotifySuspendLoading(false);
    }
  };

  const handleSuspendPTW = async () => {
    if (!id) return;

    try {
      setSuspendPTWLoading(true);

      await markRPSuspended(id);

      setShowSuspendPTWModal(false);

      setSuccessMessage('PTW suspended successfully.');
      setShowSuccess(true);

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to suspend PTW';

      setApiError(message);
      setShowError(true);
    } finally {
      setSuspendPTWLoading(false);
    }
  };

  const handleNotifyCancel = () => {
    setShowNotifyModal(false);
    setNotifyDropdownOpen(false);
    setSelectedOperator(null);
  };

  // ── Edit: open modal instead of navigating away ──
  const handleEdit = () => {
    setShowEditModal(true);
  };

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const AvatarCircle = ({ name, avatar, size = 'sm' }: { name: string; avatar?: string; size?: 'sm' | 'md' }) => {
    const dim = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm';
    if (avatar) {
      return <img src={avatar} alt={name} className={`${dim} rounded-full object-cover flex-shrink-0`} />;
    }
    return (
      <div className={`${dim} rounded-full bg-red-400 text-white flex items-center justify-center font-semibold flex-shrink-0`}>
        {getInitials(name)}
      </div>
    );
  };

  if (!id) {
    return (
      <div className="p-8">
        <p>No data found</p>
        <button onClick={() => navigate('/plant-safety')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded">
          Go Back
        </button>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader />
      </div>
    );
  }

  const statusStyle = getStatusStyle(detail.status);

  const isPendingApprover = (stageName: string) =>
    detail?.workflow_stages?.some(
      (stage: any) =>
        stage.stage === stageName &&
        stage.approvers?.some((approver: any) => Number(approver.user?.id) === Number(currentUserID) && approver.status === 'PENDING'),
    ) ?? false;

  const canAddInstruction = isPendingApprover('INSTRUCTION');
  const canMarkIsolation = isPendingApprover('ISOLATION');
  const canIssuePermit = isPendingApprover('ISSUE_PERMIT');
  const canAOCSuspend = isPendingApprover('AO_SUSPEND');
  const canAOCSign = isPendingApprover('AO_SIGN');
  const canSFTRevoke = isPendingApprover('SFT_REVOKE');
  const canAORevoke = isPendingApprover('AO_REVOKE');
  const canAOClearance = isPendingApprover('AO_CLEARANCE');

  // ── Lock Edit/Delete for the Responsible Person only once the Appointed Operator
  // has completed 'Add Instructions' (status has moved past 'Instruction Pending').
  // Staying enabled through 'New' and 'Instruction Pending' covers the RP notifying
  // the AO — that alone should not lock the record. Re-enabled automatically if a
  // later rejection resets the permit back to 'New'. ──
  const canEditOrDelete =
    Number(detail.created_by) === currentUserID && (detail.status === 'New' || detail.status === 'Instruction Pending');

  // ── Instructions may be corrected by the Appointed Person only in the window
  // between 'Add Instructions' being submitted and 'Isolation Effected' being
  // confirmed. Reuses the same pending-approver + status gate as the Isolation
  // button itself, so the edit action disappears automatically once isolation
  // is confirmed and the status advances past 'Isolation Pending'. ──
  const canEditInstructions = canMarkIsolation && detail.status === 'Isolation Pending';

  const handleMarkIsolation = async () => {
    try {
      setLoading(true);

      await markIsolation(id);

      setShowIsolationModal(false);

      fetchDetail();
    } catch (error) {
      console.error(error);
      alert('Failed to mark isolation');
    } finally {
      setLoading(false);
    }
  };

  const handleAOCClearance = async () => {
    if (!id) return;

    try {
      setAOCClearanceLoading(true);

      await markAOClearance(id);

      setShowAOCClearanceModal(false);

      setSuccessMessage('AOC clearance completed successfully.');
      setShowSuccess(true);

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to complete AOC clearance';

      setApiError(message);
      setShowError(true);
    } finally {
      setAOCClearanceLoading(false);
    }
  };

  const handleAOCSuspend = async () => {
    if (!id) return;

    try {
      setAOCSuspendLoading(true);

      await markAOCSuspended(id);

      setShowAOCSuspendModal(false);

      setSuccessMessage('Permit suspended successfully.');
      setShowSuccess(true);

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to suspend permit';

      setApiError(message);
      setShowError(true);
    } finally {
      setAOCSuspendLoading(false);
    }
  };

  const handleIssuePermitDone = async () => {
    if (!selectedIssuePermitPerson || !id) return;

    try {
      setIssuePermitLoading(true);

      const payload = {
        user_ids: [selectedIssuePermitPerson.id],
      };

      await notifyIssuePermit(id, payload);

      setShowIssuePermitModal(false);

      setSuccessMessage('Issue permit notification sent successfully.');
      setShowSuccess(true);
      fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to notify issue permit.';

      setApiError(message);
      setShowError(true);
    } finally {
      setIssuePermitLoading(false);
    }
  };

  const handleIssuePermit = async () => {
    if (!id) return;

    try {
      setIssuePermitProcessing(true);

      await markIssuePermit(id);

      setShowIssuePermitConfirmModal(false);

      setSuccessMessage('Permit issued successfully.');
      setShowSuccess(true);

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to issue permit';

      setApiError(message);
      setShowError(true);
    } finally {
      setIssuePermitProcessing(false);
    }
  };

  const handlePermitTakeOut = async () => {
    if (!id) return;

    try {
      setPermitTakeOutLoading(true);

      await markPermitTakeOut(id);

      setShowPermitTakeOutModal(false);

      setSuccessMessage('Permit take out completed successfully.');
      setShowSuccess(true);

      await fetchDetail();

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to mark permit take out';

      setApiError(message);
      setShowError(true);
    } finally {
      setPermitTakeOutLoading(false);
    }
  };

  const loadPermitDetail = async () => {
    const response = await fetchDetail();
    //setDetail(response)
  };

  const handleDownload = async () => {
    if (!id) return;

    try {
      setDownloadLoading(true);

      const blob = await getPlantSafetyWorkPermitDownload(id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PlantSafetyWorkPermit_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to download work permit.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSftDownload = async () => {
    if (!id) return;

    try {
      setSftDownloadLoading(true);

      const blob = await getSanctionForTestDownload(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SanctionForTest_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to download sanction for test.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setSftDownloadLoading(false);
    }
  };

  return (
    <>
      <Header title="Plant Safety - Work Permit Details" showBackButton />

      <div className="grid grid-cols-12 min-h-screen">
        {/* ───────────── LEFT CONTENT ───────────── */}
        <div className="col-span-8 px-6 py-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-lg">#{detail.reference_number}</h2>
              {detail.status && (
                <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 ${statusStyle.text}`}>
                  <span className={`w-2 h-2 rounded-full ${statusStyle.dot}`} />
                  {detail.status}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Notify for Isolation/Instructions */}
              {Number(detail.created_by) === currentUserID && detail.status === 'New' && (
                <button
                  onClick={() => {
                    setSelectedOperator(null);
                    setNotifyDropdownOpen(false);
                    setShowNotifyModal(true);
                  }}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Notify for Isolation/Instructions
                </button>
              )}

              {canAddInstruction && detail.status === 'Instruction Pending' && (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="LV Work Permit" id={id!} apiPath="lv-work-permits" onSuccess={loadPermitDetail} />
                  <button
                    onClick={handleOpenAddInstructions}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-md transition-colors"
                  >
                    Add Instructions
                  </button>
                </div>
              )}

              {/* Edit Instructions — available to the Appointed Person only between
                  'Add Instructions' and 'Isolation Effected' (see canEditInstructions) */}
              {canEditInstructions && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowDeleteInstructionModal(true)}
                    className="px-4 py-2 border border-red-500 text-red-600 hover:bg-red-50 text-sm font-medium rounded-md transition-colors"
                  >
                    Delete Instructions
                  </button>
                  <button
                    onClick={handleOpenEditInstructions}
                    className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors"
                  >
                    Edit Instructions
                  </button>
                </div>
              )}

              {canMarkIsolation && detail.status === 'Isolation Pending' && (
                <button
                  onClick={() => setShowIsolationModal(true)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Isolation
                </button>
              )}
              {Number(detail.created_by) === currentUserID && detail.status === 'Isolation Effected' && (
                <button
                  onClick={() => {
                    setSelectedIssuePermitPerson(null);
                    setIssuePermitDropdownOpen(false);
                    setShowIssuePermitModal(true);
                  }}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Notify for Issue Permit
                </button>
              )}
              {canIssuePermit && detail.status === 'Issue Permit Pending' && (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="LV Work Permit" id={id!} apiPath="lv-work-permits" onSuccess={loadPermitDetail} />
                  <button
                    onClick={() => setShowIssuePermitConfirmModal(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-md transition-colors"
                  >
                    Issue Permit
                  </button>
                </div>
              )}
              {Number(detail.created_by) === currentUserID && detail.status === 'Permit Issued' && (
                <button
                  onClick={() => setShowPermitTakeOutModal(true)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Permit Take Out
                </button>
              )}
              {Number(detail.created_by) === currentUserID && detail.status === 'Permit Taken Out' && (
                <>
                  <button
                    onClick={() => setShowSuspendPTWModal(true)}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Suspend PTW
                  </button>
                  <button
                    onClick={() => setShowRPClearanceModal(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Clearance
                  </button>
                </>
              )}
              {Number(detail.created_by) === currentUserID && detail.status === 'Suspended' && (
                <button
                  onClick={() => {
                    setSelectedSuspendOperator(null);
                    setNotifySuspendDropdownOpen(false);
                    setShowNotifySuspendModal(true);
                  }}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Notify to Suspend
                </button>
              )}
              {canAOCSuspend && detail.status === 'Suspension Pending' && (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="LV Work Permit" id={id!} apiPath="lv-work-permits" onSuccess={loadPermitDetail} />
                  <button
                    onClick={() => setShowAOCSuspendModal(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-md transition-colors"
                  >
                    Suspend
                  </button>
                </div>
              )}

              {Number(detail.created_by) === currentUserID &&
                detail.sanction?.status?.title === 'Revoked' &&
                detail.status === 'Suspended (AO)' && (
                  <button
                    onClick={() => setShowRPRevokeModal(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Revoke Suspension
                  </button>
                )}
              {Number(detail.created_by) === currentUserID && detail.status === 'Revoked Suspension' && (
                <button
                  onClick={() => setShowNotifyAOCRevokeModal(true)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Notify to Revoke Suspension
                </button>
              )}
              {canAORevoke && detail.status === 'Revoked Suspension Pending' && (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="LV Work Permit" id={id!} apiPath="lv-work-permits" onSuccess={loadPermitDetail} />
                  <button
                    onClick={() => setShowAOCRevokeModal(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Revoke Suspension
                  </button>
                </div>
              )}

              {Number(detail.created_by) === currentUserID && detail.status === 'Revoked Suspension (AO)' && (
                <button
                  onClick={() => setShowRPClearanceModal(true)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Clearance
                </button>
              )}

              {Number(detail.created_by) === currentUserID && detail.status === 'Clearance Done' && (
                <button
                  onClick={() => setShowAOClearanceModal(true)}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Notify for Clearance
                </button>
              )}
              {canAOClearance && detail.status === 'Clearance Pending' && (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="LV Work Permit" id={id!} apiPath="lv-work-permits" onSuccess={loadPermitDetail} />
                  <button
                    onClick={() => setShowAOCClearanceModal(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Clearance
                  </button>
                </div>
              )}
              {Number(detail.created_by) === currentUserID && detail.status === 'Clearance Done (AO)' && (
                <button
                  onClick={handleDownload}
                  disabled={downloadLoading}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-60"
                >
                  {downloadLoading ? 'Downloading...' : 'Download'}
                </button>
              )}
              {/* Delete */}
              {canEditOrDelete && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  title="Delete work permit"
                  aria-label="Delete work permit"
                  className="p-2 border rounded-md hover:bg-gray-100"
                >
                  <Trash2 size={16} />
                </button>
              )}
              {/* Edit */}
              {canEditOrDelete && (
                <button onClick={handleEdit} title="Edit" className="p-2 border rounded-md hover:bg-gray-100">
                  <Pencil size={16} />
                </button>
              )}
            </div>
          </div>

          {/* ── Application Section ── */}
          <div className="mb-6">
            <h3 className="text-blue-600 font-semibold text-base mb-4">Application</h3>

            {/* Row 1 */}
            <div className="grid grid-cols-4 gap-x-6 gap-y-4 mb-4">
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Department</p>
                <p className="font-medium text-sm">{detail.department || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Priority</p>
                <p className="font-medium text-sm">{detail.priority || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Job Number</p>
                <p className="font-medium text-sm">{detail.job_number || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">EWT</p>
                <p className="font-medium text-sm">{detail.ewt || '-'}</p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-4 gap-x-6 gap-y-4 mb-4">
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Location</p>
                <p className="font-medium text-sm">{detail.location || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Unit</p>
                <p className="font-medium text-sm">{detail.unit || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Plant Code</p>
                <p className="font-medium text-sm">{detail.plant_code || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Plant Requiring Isolation</p>
                <p className="font-medium text-sm">{detail.plant_requiring_isolation || '-'}</p>
              </div>
            </div>
          </div>

          {/* Hazards */}
          <div className="mb-6">
            <h3 className="font-medium text-sm text-gray-400 mb-3">Hazards</h3>
            {detail.hazards.length > 0 ? (
              <div
                className="text-sm text-gray-800 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1"
                dangerouslySetInnerHTML={{ __html: detail.hazards.join('') }}
              />
            ) : (
              <p className="text-sm text-gray-400">-</p>
            )}
          </div>

          {/* Work to be Carried Out */}
          <div className="mb-6">
            <h3 className="font-medium text-sm text-gray-400 mb-3">Work to be Carried Out</h3>
            {detail.work_to_be_carried_out.length > 0 ? (
              <div
                className="text-sm text-gray-800 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1"
                dangerouslySetInnerHTML={{ __html: detail.work_to_be_carried_out.join('') }}
              />
            ) : (
              <p className="text-sm text-gray-400">-</p>
            )}
          </div>

          {/* Certificates Row 1 */}
          <div className="grid grid-cols-4 gap-x-6 gap-y-4 mb-4">
            <div>
              <p className="text-gray-400 text-xs mb-0.5">PTW No.</p>
              <p className="font-medium text-sm">{detail.ptw_no || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Environment Cert No.</p>
              <p className="font-medium text-sm">{detail.environment_cert_no || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Gas Test Cert No.</p>
              <p className="font-medium text-sm">{detail.gas_test_cert_no || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Hot Work Approval Part.</p>
              <p className="font-medium text-sm">{detail.hot_work_approval || '-'}</p>
            </div>
          </div>

          {/* Certificates Row 2 */}
          <div className="grid grid-cols-4 gap-x-6 gap-y-4 mb-6">
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Risk Assessment No.</p>
              <p className="font-medium text-sm">{detail.risk_assessment_no || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Safe Entry Certificate.</p>
              <p className="font-medium text-sm">{detail.safe_entry_certificate || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Workers Register No.</p>
              <p className="font-medium text-sm">{detail.work_register_number || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">PPE</p>
              <p className="font-medium text-sm">{detail.ppe || '-'}</p>
            </div>
          </div>

          {/* Isolations/Instructions */}
          <div className="mb-6">
            {detail.isolation?.items?.length > 0 && <h3 className="text-blue-600 font-semibold text-base mb-4">Isolations/Instructions</h3>}

            {detail.isolation?.items?.length > 0 ? (
              <div className="space-y-3">
                {detail.isolation.items.map((iso: any, i: number) => {
                  const statusObj = statusList.find((s: any) => s.id === iso.status_id);
                  const keyObj = keyList.find((k: any) => k.id === iso.key_id);
                  const locationObj = locations.find((l: any) => l.id === (iso.location_id ?? detail.isolation?.location_id));
                  const plantObj = plantCodes.find((p: any) => p.id === (iso.plant_code_id ?? detail.isolation?.plant_code_id));
                  const description =
                    iso.describe_components ?? detail.isolation?.describe_components ?? detail.isolation?.decsribe_components;

                  return (
                    <div key={iso.id ?? `iso-${i}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                      {/* Header row */}
                      <button
                        onClick={() => setExpandedIsolation(expandedIsolation === i ? null : i)}
                        className="w-full flex justify-between items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          {i + 1}.{plantObj?.title && <span className="flex items-center gap-1 text-xs font-medium">{plantObj.title}</span>}
                          {statusObj?.title && (
                            <span className="flex items-center gap-1 text-xs font-medium text-orange-500">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                              {statusObj.title}
                            </span>
                          )}
                        </span>

                        <ChevronDown
                          size={16}
                          className={`text-gray-400 transition-transform ${expandedIsolation === i ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {/* Expanded content */}
                      {expandedIsolation === i && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                          {/* Info grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6 mb-5">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Location</p>
                              <p className="text-sm font-medium text-gray-900">{locationObj?.title || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Key</p>
                              <p className="text-sm font-medium text-gray-900">{keyObj?.title || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">OPS</p>
                              <p className="text-sm font-medium text-gray-900">{iso.ops || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Requirements</p>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                <input
                                  type="checkbox"
                                  checked={Boolean(detail.isolation?.requirements_checked)}
                                  readOnly
                                  className="h-4 w-4 accent-blue-600"
                                />
                                {detail.isolation?.requirements_checked ? 'Checked' : 'Not checked'}
                              </label>
                            </div>
                          </div>

                          {/* Instruction description */}
                          <div>
                            <p className="text-xs text-gray-400 mb-2">Description</p>
                            {description ? (
                              <div
                                className="text-sm text-gray-800 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1"
                                dangerouslySetInnerHTML={{ __html: description }}
                              />
                            ) : (
                              <p className="text-sm font-medium text-gray-900">-</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400">-</p>
            )}
          </div>
          {!detail?.sanction && Number(detail.created_by) === currentUserID && detail.status === 'Suspended (AO)' && (
            <SanctionForTestCard detail={detail} onSuccess={loadPermitDetail} />
          )}
          {detail?.sanction && (
            <SanctionContent
              sanction={detail.sanction}
              plantName={detail.plant_code}
              permitNumber={detail.reference_number}
              jobNumber={detail.job_number}
              permitId={detail.id}
              fetchDetail={fetchDetail}
              createdBy={Number(detail.created_by)}
              currentUserID={currentUserID}
              canAOCSign={canAOCSign}
              canSFTRevoke={canSFTRevoke}
              onDownload={handleSftDownload}
              downloadLoading={sftDownloadLoading}
            />
          )}
        </div>

        {/* ── Divider ── */}
        <div className="col-span-1 flex justify-center">
          <div className="w-px bg-gray-200 h-full" />
        </div>

        {/* ── Activities ── */}
        <div className="col-span-3 py-6 pr-6">
          <Activities moduleName="plant_safety_work_permits" moduleItemId={Number(id)} />
        </div>
      </div>

      {/* ── EDIT MODAL (reuses the same create/edit modal used on the list page) ── */}
      <CreatePlantPermitModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        editId={Number(id)}
        onSuccess={() => {
          setShowEditModal(false);
          fetchDetail();
        }}
      />

      <AOClearanceModal
        isOpen={showAOClearanceModal}
        onClose={() => setShowAOClearanceModal(false)}
        permitId={detail?.id}
        onSuccess={() => {
          fetchDetail();
          setShowAOClearanceModal(false);
        }}
      />

      {/* ── NOTIFY FOR ISSUE PERMIT MODAL ── */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
            <button onClick={handleNotifyCancel} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold mb-5">Notify for Isolation/Instructions</h2>

            {/* Select Appointed Operator */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Appointed Person</label>
              <div className="relative">
                <button
                  onClick={() => setNotifyDropdownOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2.5 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  {selectedOperator ? (
                    <span className="flex items-center gap-2">
                      <AvatarCircle name={selectedOperator.name} avatar={selectedOperator.avatar} />
                      <span>
                        <span className="font-medium text-gray-800">{selectedOperator.name}</span>
                        <span className="text-gray-400">,&nbsp;</span>
                        <span className="text-gray-500">{selectedOperator.role}</span>
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-400">Select Appointed Person </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 flex-shrink-0 transition-transform ${notifyDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {notifyDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                    {operatorsLoading ? (
                      <div className="px-4 py-2.5 text-sm text-gray-500">Loading appointed operators...</div>
                    ) : appointedOperators.length > 0 ? (
                      appointedOperators.map((op) => (
                        <button
                          key={op.id}
                          onClick={() => {
                            setSelectedOperator(op);
                            setNotifyDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                            selectedOperator?.id === op.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <AvatarCircle name={op.name} avatar={op.avatar} />
                          <span>
                            <span className="font-medium text-gray-800">{op.name}</span>
                            <span className="text-gray-400">,&nbsp;</span>
                            <span className="text-gray-500">{op.role}</span>
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2.5 text-sm text-gray-500">{operatorsError || 'No appointed operators available.'}</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleNotifyCancel}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifyDone}
                disabled={!selectedOperator || notifyLoading}
                className={`px-6 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                  selectedOperator && !notifyLoading ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 cursor-not-allowed'
                }`}
              >
                {notifyLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NOTIFY FOR ISSUE PERMIT MODAL ── */}
      {showIssuePermitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowIssuePermitModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold mb-5">Notify for Issue Permit</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Appointed Person</label>

              <div className="relative">
                <button
                  onClick={() => setIssuePermitDropdownOpen(!issuePermitDropdownOpen)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2.5 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  {selectedIssuePermitPerson ? (
                    <span>
                      {selectedIssuePermitPerson.name}
                      <span className="text-gray-400">, </span>
                      <span className="text-gray-500">{selectedIssuePermitPerson.role}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">Select Appointed Person</span>
                  )}

                  <ChevronDown size={16} className={`transition-transform ${issuePermitDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {issuePermitDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                    {appointedOperators.map((person) => (
                      <button
                        key={person.id}
                        onClick={() => {
                          setSelectedIssuePermitPerson(person);
                          setIssuePermitDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50"
                      >
                        <div className="font-medium">{person.name}</div>
                        <div className="text-xs text-gray-500">{person.role}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowIssuePermitModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleIssuePermitDone}
                disabled={!selectedIssuePermitPerson || issuePermitLoading}
                className={`px-6 py-2 rounded-md text-white text-sm font-medium ${
                  selectedIssuePermitPerson && !issuePermitLoading ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 cursor-not-allowed'
                }`}
              >
                {issuePermitLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT INSTRUCTIONS MODAL — supports adding multiple items before final submit.
          isEditingInstructions toggles title, submit label, and whether we call
          addInstruction or editInstruction on submit. */}
      {showInstructionsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[900px] max-w-[95vw] p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={resetInstructionsModalState} className="absolute right-4 top-4 text-gray-500">
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold mb-6">{isEditingInstructions ? 'Edit Instructions' : 'Add Instructions'}</h2>

            {/* Plant Code & Location (captured per item) */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm mb-1">
                  Plant Code <span className="text-red-500">*</span>
                </label>

                <select
                  value={instructionForm.plantCode}
                  onChange={(e) => {
                    setInstructionForm({
                      ...instructionForm,
                      plantCode: e.target.value,
                    });

                    setInstructionErrors({
                      ...instructionErrors,
                      plantCode: '',
                    });
                  }}
                  className={`w-full h-11 px-3 bg-[#F8F9FB] border ${
                    instructionErrors.plantCode ? 'border-red-500' : 'border-[#D9DDE5]'
                  } rounded-lg text-sm`}
                >
                  <option value="">Select Plant Code</option>

                  {plantCodes.map((plant: any) => (
                    <option key={plant.id} value={plant.id}>
                      {plant.title}
                    </option>
                  ))}
                </select>

                {instructionErrors.plantCode && <p className="text-red-500 text-sm mt-1">{instructionErrors.plantCode}</p>}
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Select Location <span className="text-red-500">*</span>
                </label>

                <select
                  value={instructionForm.location}
                  onChange={(e) => {
                    setInstructionForm({
                      ...instructionForm,
                      location: e.target.value,
                    });

                    setInstructionErrors({
                      ...instructionErrors,
                      location: '',
                    });
                  }}
                  className={`w-full h-11 px-3 bg-[#F8F9FB] border ${
                    instructionErrors.location ? 'border-red-500' : 'border-[#D9DDE5]'
                  } rounded-lg text-sm`}
                >
                  <option value="">Select Location</option>

                  {locations.map((location: any) => (
                    <option key={location.id} value={location.id}>
                      {location.title}
                    </option>
                  ))}
                </select>

                {instructionErrors.location && <p className="text-red-500 text-sm mt-1">{instructionErrors.location}</p>}
              </div>
            </div>

            {/* Description (captured per item) */}
            <div className="mb-4">
              <label className="block text-sm mb-1">Describe Components/Apparatus/Instructions</label>

              <ReactQuill
                theme="snow"
                value={instructionForm.description}
                onChange={(value, delta, source, editor) => {
                  const plainText = editor.getText(); // text only, no HTML tags
                  const length = plainText.trim().length;

                  if (length <= 200) {
                    setInstructionForm({
                      ...instructionForm,
                      description: value,
                    });
                  }
                  // if length > 200, simply don't update state — blocks further typing
                }}
                className="bg-[#F8F9FB] rounded-lg"
                modules={{
                  toolbar: [[{ list: 'ordered' }, { list: 'bullet' }], ['bold', 'italic', 'underline'], ['clean']],
                }}
              />

              <div className="flex justify-end mt-1">
                <span
                  className={`text-xs ${(() => {
                    const plainLength = instructionForm.description.replace(/<(.|\n)*?>/g, '').trim().length;
                    return plainLength >= 200 ? 'text-red-500' : 'text-gray-400';
                  })()}`}
                >
                  {instructionForm.description.replace(/<(.|\n)*?>/g, '').trim().length} / 200
                </span>
              </div>
            </div>

            {/* Requirement (shared) */}
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={instructionForm.requirement}
                  onChange={(e) =>
                    setInstructionForm({
                      ...instructionForm,
                      requirement: e.target.checked,
                    })
                  }
                  className="
                   h-4
                   w-4
                   rounded
                   border-[#D9DDE5]
                  "
                />
                Requirements
              </label>
            </div>

            {/* Edit-mode banner (per-item edit) */}
            {editingInstructionItemId !== null && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-blue-800 font-medium">✏️ Editing item</p>
                <button
                  type="button"
                  onClick={handleCancelEditInstructionItem}
                  className="text-sm text-blue-600 underline hover:text-blue-800"
                >
                  Cancel Edit
                </button>
              </div>
            )}

            {/* Status / Key (per-item) */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm mb-1">
                  Status <span className="text-red-500">*</span>
                </label>

                <select
                  value={instructionForm.statusId}
                  onChange={(e) => {
                    setInstructionForm({
                      ...instructionForm,
                      statusId: e.target.value,
                    });

                    setInstructionErrors({
                      ...instructionErrors,
                      statusId: '',
                    });
                  }}
                  className={`w-full h-11 px-3 bg-[#F8F9FB] border ${
                    instructionErrors.statusId ? 'border-red-500' : 'border-[#D9DDE5]'
                  } rounded-lg text-sm`}
                >
                  <option value="">Select Status</option>

                  {statusList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>

                {instructionErrors.statusId && <p className="text-red-500 text-sm mt-1">{instructionErrors.statusId}</p>}
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Key <span className="text-red-500">*</span>
                </label>

                <select
                  value={instructionForm.keyId}
                  onChange={(e) => {
                    setInstructionForm({
                      ...instructionForm,
                      keyId: e.target.value,
                    });

                    setInstructionErrors({
                      ...instructionErrors,
                      keyId: '',
                    });
                  }}
                  className={`w-full h-11 px-3 bg-[#F8F9FB] border ${
                    instructionErrors.keyId ? 'border-red-500' : 'border-[#D9DDE5]'
                  } rounded-lg text-sm`}
                >
                  <option value="">Select Key</option>

                  {keyList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>

                {instructionErrors.keyId && <p className="text-red-500 text-sm mt-1">{instructionErrors.keyId}</p>}
              </div>
            </div>

            {/* OPS (per-item) */}
            <div className="mb-4">
              <label className="block text-sm mb-1">
                Enter OPS <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={instructionForm.ops}
                onChange={(e) => {
                  setInstructionForm({
                    ...instructionForm,
                    ops: e.target.value,
                  });

                  setInstructionErrors({
                    ...instructionErrors,
                    ops: '',
                  });
                }}
                className={`w-full rounded-md p-2 border ${instructionErrors.ops ? 'border-red-500' : 'border-[#D9DDE5]'}`}
              />

              {instructionErrors.ops && <p className="text-red-500 text-sm mt-1">{instructionErrors.ops}</p>}
            </div>

            {/* Add / Update item button */}
            <div className="mb-6">
              <button
                type="button"
                onClick={handleAddInstructionItem}
                className="px-5 py-2 border-2 border-black rounded-md font-medium hover:bg-gray-50"
              >
                {editingInstructionItemId !== null ? 'Update Item' : 'Add Item'}
              </button>
            </div>

            {/* Added items list */}
            {instructionItems.length > 0 && (
              <div className="space-y-3 mb-6">
                {instructionItems.map((item, index) => {
                  const plantObj = plantCodes.find((p: any) => p.id === item.plant_code_id);
                  const locationObj = locations.find((l: any) => l.id === item.location_id);
                  const statusObj = statusList.find((s: any) => s.id === item.status_id);
                  const keyObj = keyList.find((k: any) => k.id === item.key_id);
                  const isExpanded = expandedInstructionItem === index;

                  return (
                    <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                      <div className="w-full flex justify-between items-center px-4 py-3 hover:bg-gray-50 transition-colors">
                        <button
                          type="button"
                          onClick={() => setExpandedInstructionItem(isExpanded ? null : index)}
                          className="flex-1 flex items-center gap-2 text-left"
                        >
                          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            {index + 1}.
                            {plantObj?.title && <span className="flex items-center gap-1 text-xs font-medium">{plantObj.title}</span>}
                            {statusObj?.title && (
                              <span className="flex items-center gap-1 text-xs font-medium text-orange-500">
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                                {statusObj.title}
                              </span>
                            )}
                          </span>
                          <ChevronDown
                            size={16}
                            className={`ml-auto text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEditInstructionItem(item)}
                          disabled={editingInstructionItemId !== null && editingInstructionItemId !== item.id}
                          className={`ml-3 px-3 py-1.5 text-sm border rounded transition-colors ${
                            editingInstructionItemId !== null && editingInstructionItemId !== item.id
                              ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                              : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          {editingInstructionItemId === item.id ? 'Editing' : 'Edit'}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleRemoveInstructionItem(item.id)}
                          className="ml-2 p-1.5 text-gray-400 hover:text-red-500"
                          title="Remove"
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 mb-4">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Location</p>
                              <p className="text-sm font-medium text-gray-900">{locationObj?.title || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Key</p>
                              <p className="text-sm font-medium text-gray-900">{keyObj?.title || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">OPS</p>
                              <p className="text-sm font-medium text-gray-900">{item.ops || '-'}</p>
                            </div>
                          </div>

                          {item.describe_components && (
                            <div>
                              <p className="text-xs text-gray-400 mb-2">Components/Apparatus/Instructions</p>
                              <div
                                className="text-sm text-gray-800 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1"
                                dangerouslySetInnerHTML={{ __html: item.describe_components }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={resetInstructionsModalState} className="px-5 py-2 border rounded-md">
                Cancel
              </button>

              <button
                onClick={handleSubmitAllInstructions}
                disabled={instructionLoading || instructionItems.length === 0 || editingInstructionItemId !== null}
                className={`px-5 py-2 rounded-md text-white ${
                  instructionLoading || instructionItems.length === 0 || editingInstructionItemId !== null
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {instructionLoading ? 'Saving...' : isEditingInstructions ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RPRevokeModal
        isOpen={showRPRevokeModal}
        onClose={() => setShowRPRevokeModal(false)}
        permitId={detail.id}
        onSuccess={() => {
          setShowRPRevokeModal(false);
          fetchDetail(); // refresh permit details
        }}
      />
      <NotifyAOCRevokeModal
        isOpen={showNotifyAOCRevokeModal}
        onClose={() => setShowNotifyAOCRevokeModal(false)}
        permitId={detail.id}
        onSuccess={() => {
          setShowNotifyAOCRevokeModal(false);
          fetchDetail();
        }}
      />
      <AOCRevokeModal
        isOpen={showAOCRevokeModal}
        onClose={() => setShowAOCRevokeModal(false)}
        permitId={detail.id}
        onSuccess={() => {
          setShowAOCRevokeModal(false);
          fetchDetail();
        }}
      />

      {showNotifySuspendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowNotifySuspendModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold mb-5">Notify to Suspend</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Appointed Person</label>

              <div className="relative">
                <button
                  onClick={() => setNotifySuspendDropdownOpen(!notifySuspendDropdownOpen)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2.5 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  {selectedSuspendOperator ? (
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">{selectedSuspendOperator.name}</span>
                      <span className="text-gray-500">{selectedSuspendOperator.role}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">Select Appointed Person</span>
                  )}

                  <ChevronDown size={16} className={`transition-transform ${notifySuspendDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {notifySuspendDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                    {appointedOperators.map((operator) => (
                      <button
                        key={operator.id}
                        onClick={() => {
                          setSelectedSuspendOperator(operator);
                          setNotifySuspendDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50"
                      >
                        <div className="font-medium">{operator.name}</div>
                        <div className="text-xs text-gray-500">{operator.role}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotifySuspendModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifySuspendDone}
                disabled={!selectedSuspendOperator || notifySuspendLoading}
                className={`px-6 py-2 rounded-md text-white text-sm font-medium ${
                  selectedSuspendOperator && !notifySuspendLoading ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 cursor-not-allowed'
                }`}
              >
                {notifySuspendLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAOCSuspendModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowAOCSuspendModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to suspend this permit?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowAOCSuspendModal(false)}
                disabled={aocSuspendLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleAOCSuspend}
                disabled={aocSuspendLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {aocSuspendLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteInstructionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6 relative">
            <button
              onClick={() => setShowDeleteInstructionModal(false)}
              disabled={deleteInstructionLoading}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 disabled:opacity-50"
            >
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-center mb-6">Are you sure you want to delete these instructions?</h2>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDeleteInstructionModal(false)}
                disabled={deleteInstructionLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInstruction}
                disabled={deleteInstructionLoading}
                className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
              >
                {deleteInstructionLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowDeleteModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-center mb-6">Are you sure you want to delete this work permit?</h2>
            <div className="flex justify-center gap-4">
              <button onClick={() => setShowDeleteModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuspendPTWModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowSuspendPTWModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to suspend this PTW?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowSuspendPTWModal(false)}
                disabled={suspendPTWLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleSuspendPTW}
                disabled={suspendPTWLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {suspendPTWLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showIssuePermitConfirmModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button
              onClick={() => setShowIssuePermitConfirmModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to issue this permit?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowIssuePermitConfirmModal(false)}
                disabled={issuePermitProcessing}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleIssuePermit}
                disabled={issuePermitProcessing}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {issuePermitProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
                {issuePermitProcessing ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  ACCEPT ISOLATION MODAL */}
      {showIsolationModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowIsolationModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to mark this permit as Isolation?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowIsolationModal(false)}
                disabled={loading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleMarkIsolation}
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NOTIFICATION SENT TOAST ── */}
      {showNotifySent && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative">
            <button onClick={() => setShowNotifySent(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Notification Sent</h4>
                <p className="text-sm">Notification successfully sent to the appointed operator.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAOCClearanceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowAOCClearanceModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to clear this permit?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowAOCClearanceModal(false)}
                disabled={aocClearanceLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleAOCClearance}
                disabled={aocClearanceLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {aocClearanceLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRPClearanceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowRPClearanceModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to clear this permit?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowRPClearanceModal(false)}
                disabled={rpClearanceLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleRPClearance}
                disabled={rpClearanceLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {rpClearanceLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPermitTakeOutModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowPermitTakeOutModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to take out this permit?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowPermitTakeOutModal(false)}
                disabled={permitTakeOutLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handlePermitTakeOut}
                disabled={permitTakeOutLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {permitTakeOutLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUCCESS NOTIFICATION ── */}
      {showSuccess && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative">
            <button onClick={() => setShowSuccess(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Success</h4>
                <p className="text-sm">{successMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ERROR NOTIFICATION ── */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlantSafetyWorkPermitDetail;
